<template>
  <NavigationBar/>
  <div class="page-container">
    <div class="form-section no-print mt-20">
      <h2>Warranty Certificate Generator</h2>
      <div class="form-grid">
        <div class="form-group"><label>Customer Name:</label><input v-model="formData.customerName" type="text"/></div>
        <div class="form-group"><label>Invoice Number:</label><input v-model="formData.invoiceNo" type="text"/></div>
        <div class="form-group"><label>Invoice Date:</label><input v-model="formData.invoiceDate" type="date"/></div>
        <div class="form-group"><label>Warranty Period (Years):</label><input v-model.number="formData.warrantyPeriod"
                                                                              type="number"/></div>
      </div>
      <div class="button-group">
        <button @click="generatePdf" :disabled="isSaving">Generate and Download PDF</button>
        <button @click="savePdf" :disabled="isSaving">
          {{ isSaving ? 'Saving...' : 'Save PDF to Server' }}
        </button>
      </div>

      <p v-if="saveMessage" class="feedback-message" :class="{ 'success': !isError, 'error': isError }">
        {{ saveMessage }}
      </p>
    </div>

    <hr class="no-print">

    <div ref="pdfContent">
      <div class="pdf-page">
        <div class="pdf-document-header">
          <div class="company-name">SUNRACK TECHNOLOGIES LLP</div>
          <div class="logo"><img src="../assets/sunrack.png" alt="Sunrack Logo"></div>
        </div>

        <div class="pdf-main-content">
          <h1>WARRANTY CERTIFICATE</h1>
          <table class="info-table">
            <tr>
              <td><strong>Certificate No:</strong></td>
              <td>SR/WC/{{ new Date().getFullYear() }}/{{ formData.invoiceNo }}</td>
              <td><strong>Date of Issue:</strong></td>
              <td>{{ new Date().toLocaleDateString('en-GB') }}</td>
            </tr>
            <tr>
              <td><strong>Invoice No:</strong></td>
              <td>{{ formData.invoiceNo }}</td>
              <td><strong>Invoice Date:</strong></td>
              <td>{{ formattedInvoiceDate }}</td>
            </tr>
            <tr>
              <td><strong>Customer Name:</strong></td>
              <td colspan="3">{{ formData.customerName }}</td>
            </tr>
            <tr>
              <td><strong>Product Name / Model No:</strong></td>
              <td colspan="3"></td>
            </tr>
            <tr>
              <td><strong>Installation Location:</strong></td>
              <td colspan="3">Latitude & Longitude</td>
            </tr>
            <tr>
              <td><strong>Warranty Start Date:</strong></td>
              <td colspan="3"></td>
            </tr>
          </table>

          <h2>1. INTRODUCTION</h2>
          <p>This Warranty Certificate ("Warranty") is provided by Sunrack Technologies LLP, here after referred as
            “SUNRACK”, a company registered in India with its corporate office at Building 4, Roop Rajat Park, Boisar,
            Maharashtra. This Warranty applies to the solar module mounting structures ("Product") manufactured and sold
            by SUNRACK to the original purchaser ("Customer") in India.</p>
          <p>This document outlines the terms, conditions, and limitations of the warranty, serving as the sole and
            exclusive warranty for the Product. The Product is designed, manufactured, and supplied in compliance with
            relevant national and international standards, including but not limited to BIS (Bureau of Indian Standards)
            guidelines concerning mechanical and structural design requirements.</p>
          <p>Validity and Issuance: This Warranty Certificate is valid only when officially issued and transmitted by
            SUNRACK through its designated, secure channels, specifically via email from a recognized corporate domain
            (@sun-rack.com) or within the official SUNRACK Warranty application. Any document claiming to be a warranty
            certificate issued or distributed through unauthorized communication platforms, including but not limited to
            WhatsApp or other unverified third-party mediums, shall be considered invalid and void.</p>

          <h2>2. WARRANTY PERIOD AND COVERAGE</h2>
          <p><strong>2.1. Limited Product Warranty: {{ formData.warrantyPeriod }} Years</strong></p>
          <p>SUNRACK warrants that the Product will be free from defects in materials and workmanship, and will perform
            its intended function, for a period of <strong>{{ warrantyPeriodInWords }} ({{ formData.warrantyPeriod }})
              years</strong> from the Warranty Start Date. This warranty covers the following aspects of the product:
          </p>
          <p><strong>2.2. Structural Integrity and Strength</strong></p>
          <p>The Product is warranted to withstand the design loads conditions (including wind loads) and environmental
            conditions specified in the original MMS technical documentation. This warranty covers any failure of the
            structure's core components those results in a collapse, significant deformation, or any failure that
            compromises the safety and functionality of the solar PV system. The design and engineering integrity of the
            structure are guaranteed to meet or exceed relevant BIS (Bureau of Indian Standards) or relevant
            international standards and guidelines mentioned in MMS technical documentation.</p>
          <p><strong>2.3. Manufacturing and Workmanship</strong></p>
          <p>This warranty covers defects arising from the manufacturing process. This includes issues such as improper
            welding, incorrect component dimensions, misaligned hole patterns, or faulty assembly of components prior to
            shipping. Any such defect that compromises the structural integrity or prevents proper installation as per
            installation manual will be covered.</p>
          <p><strong>2.4. Corrosion Warranty</strong></p>
          <p>This warranty provides comprehensive coverage against material degradation due to corrosion. This includes,
            but is not limited to:</p>
          <ul class="sub-list">
            <li><strong>2.4.1. Perforation/Through-corrosion:</strong> The failure of the MMS to prevent corrosion from
              penetrating through the entire thickness of the material.
            </li>
            <li><strong>2.4.2. Structural Integrity Compromise due to Corrosion:</strong> Any level of corrosion that
              demonstrably affects the load-bearing capacity of the mounting structure, leading to a risk of failure.
            </li>
            <li><strong>2.4.3. Flaking or Chipping of the Coating:</strong> The warranty covers instances where the
              coating flakes or peels off in large sheets, exposing the underlying steel to environmental elements and
              leading to accelerated corrosion.
            </li>
          </ul>
        </div>

        <div class="pdf-footer mt-10">
          <p>SUNRACK TECHNOLOGIES LLP | ROOP RAJAT PARK, BOISAR, MAHARASHTRA, 401501</p>
          <p>CERTIFICATE NO: SR/WC/{{ new Date().getFullYear() }}/{{ formData.invoiceNo }} | PAGE 1/3</p>
        </div>
      </div>

      <div class="pdf-page">
        <div class="pdf-document-header-secondary">
          <div class="company-name">SUNRACK TECHNOLOGIES LLP</div>
          <div class="logo"><img src="../assets/sunrack.png" alt="Sunrack Logo"></div>
        </div>

        <div class="pdf-main-content">
          <p><strong>Note:</strong> This warranty specifically extends beyond the typical Structure Raw Material
            manufacturer's warranty by covering the overall structural integrity and performance of the finished
            product, not just the raw material's resistance to perforation. We guarantee our product's ability to
            maintain its design strength and functionality, providing a more complete assurance of quality.</p>

          <h2>3. WARRANTY REMEDY</h2>
          <p>In the event of a valid warranty claim, SUNRACK, at its sole discretion, will undertake one of the
            following remedies:</p>
          <ul class="sub-list">
            <li><strong>3.1. Repair:</strong> Repair or replace the defective component(s) of the Product.</li>
            <li><strong>3.2. Replacement:</strong> Supply new, equivalent, or superior replacement component(s) to the
              Customer.
            </li>
            <li><strong>3.3. Credit:</strong> Issue a proportional credit to the Customer for the depreciated value of
              the defective Product, not to exceed the original purchase price.
            </li>
          </ul>
          <p>Any repair or replacement of a component does not extend the original warranty period. The warranty for the
            repaired or replaced part will be the remainder of the original {{ formData.warrantyPeriod }}-year warranty
            period. The Customer shall be responsible for all costs of installation, labour, and any other expenses
            associated with the repair or replacement, unless explicitly agreed upon in writing by SUNRACK.</p>

          <h2>4. WARRANTY EXCLUSIONS AND LIMITATIONS</h2>
          <p>This Warranty is null and void and does not cover any defects, damages, or failures resulting from:</p>
          <ul class="sub-list">
            <li><strong>4.1. Improper Installation:</strong> Failure to follow SUNRACK's official installation manual,
              technical drawings, or any other written instructions. This includes incorrect torque application,
              improper alignment, or the use of non-approved fasteners. MMS is not designed for submerged or prolonged
              water exposure stretching to weeks continuously.
            </li>
            <li><strong>4.2. Force Majeure and Wind Loading:</strong> Acts of God, including but not limited to
              lightning strikes, cyclones, hurricanes, tornadoes, floods, earthquakes, fires, and other extreme weather
              events not accounted for in the original design specifications for the project location. In the event of a
              structural failure alleged to be due to wind, and in the absence of verifiable, on-site meteorological
              data for the time of the event, the following process will be followed to evaluate the claim:
              <ol type="a" class="inner-list">
                <li>To properly evaluate the claim, the Customer is required to provide independent, verifiable evidence
                  demonstrating that the wind speed at the site did not exceed the design threshold of the structure.
                </li>
                <li>In the absence of such evidence, SUNRACK's engineering design calculations and the project’s
                  specified wind design parameters (as per IS 875 Part 3) will serve as the basis for our assessment.
                </li>
                <li>Claims that cannot be substantiated with evidence contrary to our design basis may not be considered
                  valid under the terms of this Warranty.
                </li>
              </ol>
            </li>
            <li><strong>4.3. External Impact & Alteration:</strong> Damage caused by third parties, intentional acts,
              vandalism, accidental damage, or any modification, alteration, or repair of the Product without prior
              written authorization from SUNRACK.
            </li>
            <li><strong>4.4. Storage and Handling of Materials:</strong> This Warranty shall be immediately voided and
              not apply to any Product or component that has been damaged due to improper storage or handling by the
              Customer or any third party after delivery. The Customer is responsible for ensuring that the Product
              materials are stored in accordance with the conditions given in Installation Manual.
            </li>
            <li><strong>4.5. Incorrect Application:</strong> Use of the Product for applications other than those
              specified in the original project design and SUNRACK’s sales Offer.
            </li>
            <li><strong>4.6. Environmental Contaminants:</strong> Exposure to aggressive chemical agents, corrosive
              atmospheres (beyond the standard C3 or C4 as per EN ISO 12944-2 unless the Product was specifically
              designed for C5-M or other severe environments), or other substances that are not considered part of a
              normal open-air installation. This includes runoff from dissimilar metals or materials that could cause
              galvanic corrosion.
            </li>
            <li><strong>4.7. Lack of Maintenance:</strong> Failure to perform routine inspections and maintenance as
              recommended in the Product's maintenance manual, including the removal of debris, dirt, or other materials
              that can trap moisture and accelerate corrosion.
            </li>
            <li><strong>4.8. Dissimilar Metals:</strong> Use of fasteners, clamps, or other components not supplied or
              approved by SUNRACK, particularly those made of bare copper or other metals that could lead to galvanic
              corrosion.
            </li>
            <li><strong>4.9. Aesthetic Issues:</strong> Minor cosmetic issues such as surface scratches, discoloration,
              or surface rust that do not affect the structural integrity or performance of the Product. The
              "self-healing" properties of MMS will often address these minor issues.
            </li>
            <li><strong>4.10. Non-Structural Components:</strong> This warranty does not cover non-structural components
              like end caps, drain clamps, cable management clips or other minor accessories that do not affect the
              load-bearing capacity of the structure.
            </li>
            <li><strong>4.11. Transfer of Ownership:</strong> This Warranty is for the benefit of the original Customer
              only and is non-transferable without the express written consent of SUNRACK.
            </li>
          </ul>
        </div>
        <div class="pdf-footer">
          <p>SUNRACK TECHNOLOGIES LLP | ROOP RAJAT PARK, BOISAR, MAHARASHTRA, 401501</p>
          <p>CERTIFICATE NO: SR/WC/{{ new Date().getFullYear() }}/{{ formData.invoiceNo }} | PAGE 2/3</p>
        </div>
      </div>

      <div class="pdf-page">
        <div class="pdf-document-header-secondary">
          <div class="company-name">SUNRACK TECHNOLOGIES LLP</div>
          <div class="logo"><img src="../assets/sunrack.png" alt="Sunrack Logo"></div>
        </div>
        <div class="pdf-main-content">
          <ul class="sub-list">
            <li><strong>4.12. Non-Payment:</strong> This Warranty shall be deemed null and void in the event of the
              Customer’s failure to make full and final payment as per the payment terms outlined in the Purchase
              Contract or Sales Order. In such a case, this Warranty is deemed to be suspended until such time as full
              payment, including any outstanding balances or late fees, has been received by SUNRACK. The warranty will
              only be reinstated upon receipt of full payment and the subsequent issuance of an official written
              confirmation from SUNRACK to the Customer. The Warranty Start Date remains as per the original certificate
              and will not be extended.
            </li>
          </ul>
          <h2>5. WARRANTY CLAIM PROCEDURE</h2>
          <p>To file a warranty claim, the Customer must follow the procedure below:</p>
          <ul class="sub-list">
            <li><strong>5.1. Written Notification:</strong> The Customer must notify SUNRACK in writing of the alleged
              defect within thirty (30) days of its discovery. The notification must include:
              <ul class="inner-list">
                <li>This Warranty Certificate number and the original invoice number.</li>
                <li>The name and address of the Customer and the project location.</li>
                <li>A detailed description of the defect, including the specific part(s) and a clear explanation of the
                  issue.
                </li>
                <li>Supporting documentation, including photographs and/or videos of the defective Product.</li>
              </ul>
            </li>
            <li><strong>5.2. Product Inspection:</strong> SUNRACK reserves the right to inspect the Product at the
              installation site. The Customer must grant access and cooperation to SUNRACK or its authorized
              representative for such an inspection.
            </li>
            <li><strong>5.3. Claim Verification:</strong> SUNRACK will evaluate the claim based on the provided
              information and its inspection. The final decision on the validity of the claim rests solely with SUNRACK.
            </li>
          </ul>
          <h2>6. DISCLAIMERS AND JURISDICTION</h2>
          <p><strong>6.1. Limitation of Liability:</strong> In no event shall SUNRACK be liable for any incidental,
            consequential, special, punitive, or indirect damages, including but not limited to loss of revenue,
            profits, or any other financial loss, arising from the use or inability to use the Product, even if SUNRACK
            has been advised of the possibility of such damages. The total liability of SUNRACK under this Warranty
            shall be limited to the original purchase price of the defective Product.</p>
          <p><strong>6.2. Jurisdiction:</strong> This Warranty shall be governed by and construed in accordance with the
            laws of India.</p>
          <p><strong>6.3. Dispute Resolution:</strong> Any dispute arising out of or in connection with this Warranty,
            including any question regarding its existence, validity, or termination, shall be first attempted to be
            resolved amicably through good-faith discussions between the parties.</p>
          <p><strong>6.4. Governing Courts:</strong> If a dispute cannot be resolved amicably, the parties agree that
            such dispute shall be submitted to the exclusive jurisdiction of the competent courts in Mumbai, India.</p>
          <p>This Warranty is the complete and exclusive statement of warranty for the Product. No other warranties,
            expressed or implied, including the implied warranties of merchantability and fitness for a particular
            purpose, shall apply.</p>

          <div class="signature-block">
            <p><strong>SUNRACK TECHNOLOGIES LLP</strong></p>
            <div class="signature-line">[Authorized Signatory Name]</div>
            <div class="signature-line">[Designation]</div>
            <div class="signature-line">[Date]</div>
          </div>

          <div class="acknowledgement">
            <h3>ACKNOWLEDGEMENT (FOR CUSTOMER ONLY)</h3>
            <p>"I, [{{ formData.customerName }}], hereby acknowledge receipt of the Warranty Certificate (Certificate
              No. [SR/WC/{{ new Date().getFullYear() }}/{{ formData.invoiceNo }}]) for the Sunrack Technologies LLP’s
              Solar Mounting Structure, and I confirm that I have read, understood, and accept all the terms,
              conditions, and limitations contained therein."</p>
          </div>
        </div>
        <div class="pdf-footer">
          <p>SUNRACK TECHNOLOGIES LLP | ROOP RAJAT PARK, BOISAR, MAHARASHTRA, 401501</p>
          <p>CERTIFICATE NO: SR/WC/{{ new Date().getFullYear() }}/{{ formData.invoiceNo }} | PAGE 3/3</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2pdf from 'html2pdf.js';
import {toWords} from 'number-to-words';
import axios from "axios";
import NavigationBar from "@/components/NavigationBar.vue";

export default {
  components: {
    NavigationBar
  },
  data() {
    return {
      formData: {
        customerName: 'As mentioned in Invoice',
        invoiceNo: '12345',
        invoiceDate: new Date().toISOString().substr(0, 10),
        warrantyPeriod: 25,
      },
      //
      isSaving: false,
      saveMessage: '',
      isError: false,
    };
  },
  computed: {
    warrantyPeriodInWords() {
      return this.formData.warrantyPeriod ? toWords(this.formData.warrantyPeriod) : '';
    },
    formattedInvoiceDate() {
      if (!this.formData.invoiceDate) return '';
      const [year, month, day] = this.formData.invoiceDate.split('-');
      return `${day}-${month}-${year}`;
    }
  },
  methods: {
    generatePdf() {
      const element = this.$refs.pdfContent;
      const opt = {
        // KEY FIX: Reduced top/left/right margins for a wider content area
        margin: [0.3, 0.35, 0.4, 0.35], // [top, left, bottom, right] in inches
        filename: `Warranty_${this.formData.invoiceNo}.pdf`,
        image: {type: 'jpeg', quality: 0.98},
        html2canvas: {scale: 2, useCORS: true},
        jsPDF: {unit: 'in', format: 'a4', orientation: 'portrait'}
      };
      html2pdf().from(element).set(opt).save();
    },
    async savePdf() {
      this.isSaving = true;
      this.saveMessage = '';
      this.isError = false;

      const element = this.$refs.pdfContent;
      const opt = {
        margin: [0.3, 0.35, 0.4, 0.35],
        filename: `Warranty_${this.formData.invoiceNo}.pdf`,
        image: {type: 'jpeg', quality: 0.98},
        html2canvas: {scale: 2, useCORS: true},
        jsPDF: {unit: 'in', format: 'a4', orientation: 'portrait'}
      };

      try {
        // 1. Generate the PDF as a Blob object in memory
        const pdfBlob = await html2pdf().from(element).set(opt).output('blob');
        const userInfo = JSON.parse(localStorage.getItem("user-info"));
        const username = userInfo?.username || "unknown";

        // 2. Create a FormData object to send the file
        const formData = new FormData();
        const fileName = `Warranty_${this.formData.invoiceNo}.pdf`;
        formData.append('file', pdfBlob, fileName);
        formData.append('sr', this.$route.query.sr);
        formData.append('kit_name', this.$route.query.kit_name);
        formData.append('client_name', this.$route.query.client_name);
        formData.append('created_by', username);

        // 3. POST the FormData to your Django ViewSet endpoint
        // NOTE: Ensure your axios instance is configured to send auth headers
        await axios.post('/created-warranties/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // 4. Handle the success response
        this.saveMessage = "Warranty PDF saved successfully!";

      } catch (error) {
        // 5. Handle any errors
        this.isError = true;
        this.saveMessage = 'Failed to save PDF. Please check the console and try again.';
        console.error('Error saving PDF:', error);
      } finally {
        this.isSaving = false;
      }
    },
  },
  mounted() {
    console.log("Received Kit Name:", this.$route.query.kit_name);
    console.log("Received SR:", this.$route.query.sr);
    console.log("Received Client:", this.$route.query.client_name);
  },
};
</script>

<style scoped>
/* Scoped styles for the component */
.page-container {
  max-width: 1200px;
  margin: 0 auto;
}

.form-section {
  padding: 2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 2rem;
  background-color: #f9f9f9;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
}

.form-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

button {
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

@media print {
  .no-print {
    display: none;
  }
}

/* Styles for the PDF page itself */
.pdf-page {
  background: white;
  position: relative;
  padding: 0.4in; /* Padding for screen preview */
  margin: 0 auto 1rem auto;
  box-sizing: border-box;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
  color: #333;
  /* KEY FIX: Tighter line height and smaller font size */
  line-height: 1.25;
  font-size: 9pt;
  page-break-after: always;
}

.pdf-document-header, .pdf-document-header-secondary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding-bottom: 0.4rem;
  margin-bottom: 0.8rem;
}

.company-name {
  font-weight: bold;
  font-size: 12pt;
}

.logo img {
  max-height: 35px;
  padding-bottom: 3px;
}

h1 {
  font-size: 15pt;
  text-align: center;
  font-weight: 700;
  margin-bottom: 0.8rem;
  letter-spacing: 1px;
}

h2 {
  font-size: 10pt;
  font-weight: bold;
  margin-top: 0.6rem;
  margin-bottom: 0.2rem;
}

p {
  margin-bottom: 0.2rem;
}

.sub-list {
  list-style-type: none;
  padding-left: 0;
}

.sub-list > li {
  margin-bottom: 0.2rem;
}

.inner-list {
  list-style-type: disc;
  margin-top: 0.2rem;
  padding-left: 20px;
}

ol.inner-list {
  list-style-type: lower-alpha;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0.8rem;
  font-size: 9pt;
}

.info-table td {
  border: 1px solid #ccc;
  padding: 0.4rem;
  vertical-align: top;
}

.info-table strong {
  font-weight: 600;
}

.signature-block {
  margin-top: 1.2rem;
}

.signature-block .signature-line {
  border-top: 1px solid #555;
  margin-top: 1.2rem;
  padding-top: 0.4rem;
  display: inline-block;
  width: 180px;
  margin-right: 1.5rem;
}

.acknowledgement {
  margin-top: 1.2rem;
  border-top: 1px solid #ccc;
  padding-top: 0.8rem;
}

.acknowledgement h3 {
  font-size: 10pt;
  text-align: center;
}

.pdf-footer {
  position: absolute;
  bottom: 0;
  left: 0.4in;
  right: 0.4in;
  text-align: center;
  font-size: 8pt;
  color: #666;
  border-top: 1px solid #eee;
  padding-top: 0.2rem;
}

.pdf-footer p {
  margin: 0;
}

.button-group {
  margin-top: 1.5rem;
  display: flex;
  gap: 1rem;
}

button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.feedback-message {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 4px;
  font-weight: bold;
}

.feedback-message.success {
  color: #155724;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
}

.feedback-message.error {
  color: #721c24;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
}
</style>